import React, { useEffect, useState } from 'react';
import { X, Loader2, Plus, Trash2 } from 'lucide-react';
import { getNamespaces, createSecrets } from '../services/api';

const emptyRow = () => ({ key: '', value: '' });

const CreateSecretsModal = ({ onClose, onCreated }) => {
  const [namespaces, setNamespaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [namespaceId, setNamespaceId] = useState('');
  const [secretName, setSecretName] = useState('');
  const [certificate, setCertificate] = useState('');
  const [rows, setRows] = useState([emptyRow()]);

  useEffect(() => {
    getNamespaces()
      .then((res) => setNamespaces(res.data.namespaces || []))
      .catch(() => setError('Failed to load namespaces'))
      .finally(() => setLoading(false));
  }, []);

  const selectedNamespace = namespaces.find((n) => n.id === namespaceId);

  const updateRow = (index, field) => (e) => {
    const next = [...rows];
    next[index] = { ...next[index], [field]: e.target.value };
    setRows(next);
  };

  const addRow = () => setRows((r) => [...r, emptyRow()]);
  const removeRow = (index) => setRows((r) => r.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!namespaceId) {
      setError('Select a namespace.');
      return;
    }
    if (!secretName.trim()) {
      setError('Enter a secret name.');
      return;
    }
    if (!certificate.trim()) {
      setError('Paste the Sealed Secrets certificate (PEM).');
      return;
    }

    const secrets = {};
    for (const row of rows) {
      if (!row.key.trim()) continue;
      secrets[row.key.trim()] = row.value;
    }
    if (Object.keys(secrets).length === 0) {
      setError('Add at least one key/value pair.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await createSecrets({
        namespaceId,
        secretName: secretName.trim(),
        secrets,
        certificate: certificate.trim(),
      });
      onCreated?.(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create secrets');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white text-gray-900 rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Create Secrets</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="px-6 py-8 text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="animate-spin" size={14} /> Loading…
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {error && <div className="bg-red-50 text-red-700 text-sm rounded p-3">{error}</div>}

            <div>
              <label className="block text-sm font-medium mb-1">Namespace</label>
              {namespaces.length === 0 ? (
                <div className="text-sm text-amber-700 bg-amber-50 rounded p-3">
                  No namespaces are allocated to your company yet. Contact KaydSystems support.
                </div>
              ) : (
                <select
                  required
                  value={namespaceId}
                  onChange={(e) => setNamespaceId(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">Select a namespace…</option>
                  {namespaces.map((ns) => (
                    <option key={ns.id} value={ns.id}>
                      {ns.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Secret name</label>
              <input
                type="text"
                required
                value={secretName}
                onChange={(e) => setSecretName(e.target.value)}
                placeholder="e.g. backend-secret"
                className="w-full border rounded px-3 py-2 text-sm font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                To attach this to an app, reference this name in that app's deployment
                (envFrom.secretRef.name).
              </p>
            </div>

            {selectedNamespace && (
              <div className="text-xs bg-gray-50 rounded p-3 text-gray-600">
                This will be sealed and written to namespace{' '}
                <span className="font-medium">{selectedNamespace.name}</span>. Confirm this is
                correct before submitting.
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Sealed Secrets certificate (PEM)</label>
              <textarea
                required
                value={certificate}
                onChange={(e) => setCertificate(e.target.value)}
                rows={4}
                placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                className="w-full border rounded px-3 py-2 text-xs font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Fetch once with <code>kubeseal --fetch-cert</code> and paste the contents here.
                This is the controller's public certificate — not a secret itself.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Key / value pairs</label>
              <div className="space-y-2">
                {rows.map((row, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      type="text"
                      placeholder="KEY"
                      value={row.key}
                      onChange={updateRow(i, 'key')}
                      className="w-1/3 border rounded px-3 py-2 text-sm font-mono"
                    />
                    <input
                      type="password"
                      placeholder="value"
                      value={row.value}
                      onChange={updateRow(i, 'value')}
                      className="flex-1 border rounded px-3 py-2 text-sm font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      disabled={rows.length === 1}
                      className="px-2 text-gray-400 hover:text-red-600 disabled:opacity-30"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addRow}
                className="mt-2 text-sm text-blue-600 flex items-center gap-1"
              >
                <Plus size={14} /> Add another
              </button>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded border text-gray-700 border-gray-300 hover:bg-gray-50">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || namespaces.length === 0}
                className="px-4 py-2 text-sm rounded bg-blue-600 text-white disabled:opacity-50 flex items-center gap-2"
              >
                {submitting && <Loader2 className="animate-spin" size={14} />}
                Create Secrets
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateSecretsModal;
